import { jwtDecode } from "jwt-decode";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getLoginPath } from "~/features/auth/routes";
import type { Maybe } from "~/features/common/types/types";
import type { JwtInterface } from "~/features/common/utils/loginChecks";
import {
  type GetEditableContestantsForUserQuery,
  type GetEditableContestantsForUserQueryVariables,
  GetEditableContestantsForUserDocument,
} from "~/generated/graphql";
import { fetcher } from "~/graphql/fetcher";
import { queryClient } from "~/graphql/queryClient";
import config from "../../../config";
import type { AuthInterface } from "../../common/context/AuthContext";

function useProvideAuth(): AuthInterface {
  const navigate = useNavigate();
  const [user, setUser] = useState<Maybe<string>>(() => config.get("security.jwt"));

  async function signin(token: string, cb: () => void) {
    config.set("security.jwt", token);

    const decoded = jwtDecode<JwtInterface>(token);

    // fetcher?
    const { person } = await fetcher<GetEditableContestantsForUserQuery, GetEditableContestantsForUserQueryVariables>(
      GetEditableContestantsForUserDocument,
      { id: decoded.sub },
    )();

    config.set("security.editableContestantIDs", person?.editableContestantIDs ?? []);
    config.set("security.visibleContestantIDs", person?.visibleContestantIDs ?? []);
    setUser(token);
    setTimeout(cb, 100);
  }

  function signout() {
    const jwt = config.get("security.jwt");
    if (jwt !== "") {
      config.set("security.jwt", "");
      config.set("security.editableContestantIDs", null);
      config.set("security.visibleContestantIDs", null);
    }
    queryClient.cancelQueries();
    queryClient.clear();
    setUser(null);

    setTimeout(() => {
      navigate(getLoginPath({}));
    }, 100);
  }

  config.onDidChange("security.jwt", () => {
    const jwt = config.get("security.jwt");
    if (jwt === "" || jwt == null) {
      signout();
    }
  });

  return {
    user,
    isLoggedIn: !!user,
    signin,
    signout,
  };
}

export default useProvideAuth;
